import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import InlineSelect from '../../../components/InlineSelect';
import ComboTabellata from '../../../components/ComboTabellata';
import ComboFiltro from '../../../components/ComboFiltro';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatCurrency, formatQuantity } from '../../../utils/formatters';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { API_DOCS } from './ArticoliPage.api';

// Icone SVG
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Tipi
interface UnitaMisura { id: number; simbolo: string; nome: string; }

interface Articolo {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita: { id: number; simbolo: string; nome: string } | null;
  prezzoStandard: number | null;
  prezzoListinoVendita: number | null;
  qtaMinOrdinabile: number | null;
  marca: { id: number; nome: string } | null;
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null } | null;
  ecommerce: boolean;
  perPreventivi: boolean;
  cartellaImmagini: string | null;
  attivo: boolean;
}

interface SelectItem { id: number; nome: string; }
interface Fornitore { id: number; ragioneSociale: string | null; nome: string | null; }

interface ModalData {
  tipologia: string;
  codice: string;
  descrizione: string;
  umVenditaId: number;
  societaId: string;
  prodottoId: number | null;
}

// Costanti statiche
const TIPOLOGIE = [
  { value: 'PRODOTTO_CONFIGURATO', label: 'Prodotto configurato' },
  { value: 'ARTICOLO_FISICO', label: 'Articolo fisico' },
  { value: 'LAVORAZIONE_PRESTAZIONE', label: 'Lavorazione/Prestazione' },
  { value: 'INTERCAMBIABILE', label: 'Intercambiabile' },
  { value: 'RAGGRUPPAMENTO_PREZZO_CORPO', label: 'Raggruppamento (prezzo a corpo)' },
  { value: 'LICENZA', label: 'Licenza' },
  { value: 'CANONE', label: 'Canone' },
  { value: 'CONSUMI', label: 'Consumi' },
  { value: 'COMMISSIONI', label: 'Commissioni' },
];

const TIPOLOGIE_LABEL: Record<string, string> = {
  PRODOTTO_CONFIGURATO: 'Prodotto configurato',
  ARTICOLO_FISICO: 'Articolo fisico',
  LAVORAZIONE_PRESTAZIONE: 'Lavorazione/Prestazione',
  INTERCAMBIABILE: 'Intercambiabile',
  RAGGRUPPAMENTO_PREZZO_CORPO: 'Raggruppamento (prezzo a corpo)',
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  CONSUMI: 'Consumi',
  COMMISSIONI: 'Commissioni',
};

// Items statici per ComboFiltro
const TIPOLOGIE_ITEMS = TIPOLOGIE.map(t => ({ id: t.value, nome: t.label }));

const DEFAULT_FILTRI = {
  q: '',
  tipologia: '',
  fornitoreId: '',
  marcaId: '',
  collezioneId: '',
  formatoId: '',
  umVenditaId: '',
  umAcquistoId: '',
  ecommerce: null as boolean | null,
  perPreventivi: null as boolean | null,
  categoriaMerceologicaId: null as number | null,
  categoriaConfigurazioneId: null as number | null,
  effettoId: null as number | null,
  stato: 'attivi' as 'attivi' | 'tutti' | 'disattivi',
};

const LIMIT = 25;

// ── Nodo 1: Prodotto + tipologie ricorrenti ─────────────────────────
const TIPOLOGIE_RICORRENTI = ['LICENZA', 'CANONE', 'CONSUMI', 'COMMISSIONI'] as const;
const isRicorrente = (t: string) => (TIPOLOGIE_RICORRENTI as readonly string[]).includes(t);

interface ProdottoOption { id: number; codice: string; nome: string; }

export default function ArticoliPage() {
  const navigate = useNavigate();
  const { societaId: filterSocietaId, setSocietaId: setFilterSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [filtri, setFiltri] = useState(DEFAULT_FILTRI);
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [collezioni, setCollezioni] = useState<SelectItem[]>([]);
  const [umList, setUmList] = useState<UnitaMisura[]>([]);
  const [prodottiOptions, setProdottiOptions] = useState<ProdottoOption[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({ tipologia: '', codice: '', descrizione: '', umVenditaId: 0, societaId: '', prodottoId: null });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Carica dati per i filtri (fornitori, unità di misura)
  const caricaFiltri = useCallback(async () => {
    const qs = filterSocietaId ? `?societaId=${filterSocietaId}` : '';
    try {
      const [fornitoriRes, umRes] = await Promise.all([
        // @api GET /api/nominativi -> {"data":[{"id":1,"ragioneSociale":"..."}],"total":0}
        apiFetch<{ data: Fornitore[]; total: number }>(`/api/nominativi?ruolo=FORNITORE&limit=200${filterSocietaId ? `&societaId=${filterSocietaId}` : ''}`),
        // @api GET /api/anagrafiche/articoli/unita-misura -> {"success":true,"data":[{"id":1,"simbolo":"mq","nome":"metri quadrati"}]}
        apiFetch<{ success: boolean; data: UnitaMisura[] }>(`/api/anagrafiche/articoli/unita-misura${qs}`),
      ]);
      setFornitori(fornitoriRes.data);
      setUmList(umRes.data);
    } catch (err) {
      console.error('[ArticoliPage] caricaFiltri', err);
    }
  }, [filterSocietaId]);

  // Carica articoli con i filtri applicati
  const doSearch = useCallback(async (searchFiltri: typeof DEFAULT_FILTRI, p: number) => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      if (searchFiltri.q) params.set('q', searchFiltri.q);
      if (searchFiltri.tipologia) params.set('tipologia', searchFiltri.tipologia);
      if (searchFiltri.fornitoreId) params.set('fornitoreId', searchFiltri.fornitoreId);
      if (searchFiltri.marcaId) params.set('marcaId', searchFiltri.marcaId);
      if (searchFiltri.collezioneId) params.set('collezioneId', searchFiltri.collezioneId);
      if (searchFiltri.formatoId) params.set('formatoId', searchFiltri.formatoId);
      if (searchFiltri.umVenditaId)  params.set('umVenditaId',  searchFiltri.umVenditaId);
      if (searchFiltri.umAcquistoId) params.set('umAcquistoId', searchFiltri.umAcquistoId);
      if (searchFiltri.ecommerce !== null) params.set('ecommerce', String(searchFiltri.ecommerce));
      if (searchFiltri.perPreventivi !== null) params.set('perPreventivi', String(searchFiltri.perPreventivi));
      if (searchFiltri.categoriaMerceologicaId)   params.set('categoriaMerceologicaId',   String(searchFiltri.categoriaMerceologicaId));
      if (searchFiltri.categoriaConfigurazioneId) params.set('categoriaConfigurazioneId', String(searchFiltri.categoriaConfigurazioneId));
      if (searchFiltri.effettoId)                 params.set('effettoId',                 String(searchFiltri.effettoId));
      if (searchFiltri.stato === 'tutti')         params.set('includiDisattivi',          'true');
      else if (searchFiltri.stato === 'disattivi') params.set('attivo',                   'false');
      // @api GET /api/anagrafiche/articoli -> {"success":true,"data":[{"id":1,"codice":"..."}],"total":0,"page":1,"limit":25}
      const res = await apiFetch<{ success: boolean; data: Articolo[]; total: number; page: number; limit: number }>(`/api/anagrafiche/articoli?${params}`);
      setArticoli(res.data);
      setTotal(res.total);
      setCurrentPage(p);
    } catch (err) {
      console.error('[ArticoliPage] doSearch', err);
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId]);

  // Avvio caricamento quando la societaId è pronta
  useEffect(() => {
    if (pronta) {
      caricaFiltri();
      doSearch(DEFAULT_FILTRI, 1);
    }
  }, [pronta, filterSocietaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aggiorna collezioni quando cambia la marca nei filtri
  useEffect(() => {
    if (!filtri.marcaId) {
      setCollezioni([]);
      return;
    }
    // @api GET /api/anagrafiche/articoli/collezioni -> {"success":true,"data":[{"id":1,"nome":"..."}]}
    apiFetch<{ success: boolean; data: SelectItem[] }>(`/api/anagrafiche/articoli/collezioni?marcaId=${filtri.marcaId}${filterSocietaId ? `&societaId=${filterSocietaId}` : ''}`)
      .then(r => setCollezioni(r.data))
      .catch(() => setCollezioni([]));
  }, [filtri.marcaId]);

  // Carica prodotti quando l'utente sceglie una tipologia ricorrente nella modal
  useEffect(() => {
    if (!showModal) return;
    if (!isRicorrente(modalData.tipologia)) return;

    const sid = modalData.societaId || filterSocietaId;
    if (!sid) return;

    // @api GET /api/anagrafiche/prodotti -> {"success":true,"data":[{"id":1,"codice":"...","nome":"..."}]}
    apiFetch<{ success: boolean; data: ProdottoOption[] }>(`/api/anagrafiche/prodotti?societaId=${sid}`)
      .then(r => setProdottiOptions(r.data))
      .catch(err => {
        console.error('[ArticoliPage] caricaProdotti', err);
        setProdottiOptions([]);
      });
  }, [showModal, modalData.tipologia, modalData.societaId, filterSocietaId]);

  // Reset prodottoId se la tipologia non è più ricorrente
  useEffect(() => {
    if (!showModal) return;
    if (!isRicorrente(modalData.tipologia) && modalData.prodottoId !== null) {
      setModalData(prev => ({ ...prev, prodottoId: null }));
    }
  }, [modalData.tipologia, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCerca = () => doSearch(filtri, 1);

  const handleReset = () => {
    setFiltri(DEFAULT_FILTRI);
    doSearch(DEFAULT_FILTRI, 1);
  };

  // ── Modal ──────────────────────────────────────────────────────────────────

  const handleOpenModal = () => {
    setModalData({ tipologia: '', codice: '', descrizione: '', umVenditaId: 0, societaId: filterSocietaId || '', prodottoId: null });
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setSaving(false);
  };

  const handleSuggerisci = async () => {
    const radice = modalData.codice.trim().toUpperCase();
    if (radice.length < 2) {
      setError('Digita una radice di almeno 2 caratteri');
      return;
    }
    const sid = modalData.societaId || filterSocietaId;
    if (!sid) {
      setError('Seleziona prima la società');
      return;
    }
    try {
      // @api GET /api/anagrafiche/articoli/prossimo-codice -> {"success":true,"codice":"..."}
      const res = await apiFetch<{ success: boolean; codice: string }>(
        `/api/anagrafiche/articoli/prossimo-codice?societaId=${sid}&radice=${encodeURIComponent(radice)}`
      );
      setModalData(prev => ({ ...prev, codice: res.codice }));
      setError('');
    } catch (err: any) {
      setError(err.message || 'Errore nel suggerimento codice');
    }
  };

  const handleCreaArticolo = async () => {
    if (!modalData.tipologia) { setError('Tipologia è obbligatoria'); return; }
    if (!modalData.codice.trim()) { setError('Codice è obbligatorio'); return; }
    if (!modalData.descrizione.trim()) { setError('Descrizione base è obbligatoria'); return; }
    if (!modalData.umVenditaId) { setError('U.M. Vendita è obbligatoria'); return; }
    const sid = modalData.societaId || filterSocietaId;
    if (!sid) { setError('Società è obbligatoria'); return; }

    // Validazione Prodotto obbligatorio per tipologie ricorrenti (Nodo 1)
    if (isRicorrente(modalData.tipologia) && !modalData.prodottoId) {
      setError('Prodotto obbligatorio per tipologie LICENZA / CANONE / CONSUMI / COMMISSIONI');
      return;
    }

    try {
      setSaving(true);
      setError('');
      // @api POST /api/anagrafiche/articoli {"societaId":1,"tipologia":"ARTICOLO_FISICO","codice":"...","descrizioneBase":"...","umVenditaId":1,"prodottoId":1} -> {"success":true,"data":{"id":1}}
      const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/anagrafiche/articoli', {
        method: 'POST',
        body: JSON.stringify({
          societaId: parseInt(sid),
          tipologia: modalData.tipologia,
          codice: modalData.codice.trim().toUpperCase(),
          descrizioneBase: modalData.descrizione.trim(),
          umVenditaId: modalData.umVenditaId,
          ...(modalData.prodottoId ? { prodottoId: modalData.prodottoId } : {}),
        }),
      });
      handleCloseModal();
      navigate(`/anagrafiche/articoli/${res.data.id}`);
    } catch (err: any) {
      setError(err.message || 'Errore nella creazione dell\'articolo');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const fornitoreNome = (f: Articolo['fornitore']) => f ? (f.ragioneSociale || f.nome || '-') : '-';

  const columns = useMemo<BopExcelColumn<Articolo>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (a) => a.codice,
      filter: { type: 'text' },
      width: '140px',
      cellClassName: 'articoli-codice',
      render: (a) => (
        <>
          {a.codice}
          {!a.attivo && (
            <span className="bop-badge bop-badge-danger articoli-codice-badge">Disattivo</span>
          )}
        </>
      ),
    },
    {
      key: 'descrizioneBase',
      header: 'Descrizione',
      accessor: (a) => a.descrizioneBase,
      filter: { type: 'text' },
      render: (a) => a.descrizioneBase,
    },
    {
      key: 'tipologia',
      header: 'Tipologia',
      accessor: (a) => a.tipologia,
      filter: {
        type: 'enum',
        options: TIPOLOGIE.map(t => ({ value: t.value, label: t.label })),
      },
      width: '180px',
      render: (a) => (
        <span className="bop-badge bop-badge-info">
          {TIPOLOGIE_LABEL[a.tipologia] || a.tipologia}
        </span>
      ),
    },
    {
      key: 'umVendita',
      header: 'U.M. Vendita',
      accessor: (a) => a.umVendita?.simbolo || '',
      filter: { type: 'text' },
      width: '110px',
      render: (a) => a.umVendita?.simbolo || '-',
    },
    {
      key: 'fornitore',
      header: 'Fornitore',
      accessor: (a) => fornitoreNome(a.fornitore),
      filter: { type: 'text' },
      render: (a) => fornitoreNome(a.fornitore),
    },
    {
      key: 'prezzoStandard',
      header: 'Prezzo Standard',
      accessor: (a) => a.prezzoStandard != null ? Number(a.prezzoStandard) : null,
      filter: { type: 'number' },
      width: '140px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (a) => a.prezzoStandard != null
        ? formatCurrency(Number(a.prezzoStandard))
        : '-',
    },
    {
      key: 'prezzoListinoVendita',
      header: 'Prezzo U.M. Vendita',
      accessor: (a) => a.prezzoListinoVendita != null ? Number(a.prezzoListinoVendita) : null,
      filter: { type: 'number' },
      width: '150px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (a) => a.prezzoListinoVendita != null
        ? formatCurrency(Number(a.prezzoListinoVendita))
        : '-',
    },
    {
      key: 'qtaMinOrdinabile',
      header: 'Qta min vendita',
      accessor: (a) => a.qtaMinOrdinabile != null ? Number(a.qtaMinOrdinabile) : null,
      filter: { type: 'number' },
      width: '140px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (a) => a.qtaMinOrdinabile != null
        ? `${formatQuantity(Number(a.qtaMinOrdinabile))}${a.umVendita?.simbolo ? ` ${a.umVendita.simbolo}` : ''}`
        : '-',
    },
  ], []);

  return (
    <Layout>
      <div className="articoli-page">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Anagrafica Articoli</h1>
          <button className="bop-btn-primary" onClick={handleOpenModal}>
            + Nuovo Articolo
          </button>
        </div>

        {/* Filtri */}
        <div className="bop-card articoli-filtri">

          {/* Riga 1: Ricerca | Tipologia | Fornitore */}
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">Ricerca (codice o descrizione)</label>
              <input
                type="text"
                className="bop-field__input"
                value={filtri.q}
                onChange={(e) => setFiltri({ ...filtri, q: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleCerca()}
                placeholder="Cerca per codice o descrizione..."
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Tipologia</label>
              <ComboFiltro
                label="Tipologia"
                value={filtri.tipologia || null}
                onChange={(id) => setFiltri(prev => ({ ...prev, tipologia: id ?? '' }))}
                items={TIPOLOGIE_ITEMS}
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Fornitore</label>
              <ComboFiltro
                label="Fornitore"
                value={filtri.fornitoreId || null}
                onChange={(id) => setFiltri(prev => ({ ...prev, fornitoreId: id ?? '' }))}
                items={fornitori.map(f => ({ id: String(f.id), nome: f.ragioneSociale || f.nome || '' }))}
              />
            </div>
          </div>

          {/* Riga 2: Marca (ComboTabellata) | Collezione | Formato (ComboTabellata) | E-commerce */}
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">Marca</label>
              {/* @api GET /api/anagrafiche/articoli/marche -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
              {/* @api POST /api/anagrafiche/articoli/marche {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
              <ComboTabellata
                label="Marca"
                value={filtri.marcaId ? parseInt(filtri.marcaId, 10) : null}
                onChange={(id) => setFiltri(prev => ({ ...prev, marcaId: String(id ?? ''), collezioneId: '' }))}
                societaId={filterSocietaId ? parseInt(filterSocietaId, 10) : 0}
                endpoint="/api/anagrafiche/articoli/marche"
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Collezione</label>
              <select
                className="bop-field__select"
                value={filtri.collezioneId}
                onChange={(e) => setFiltri({ ...filtri, collezioneId: e.target.value })}
                disabled={!filtri.marcaId}
              >
                <option value="">Tutte</option>
                {collezioni.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Formato</label>
              {/* @api GET /api/anagrafiche/articoli/formati -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
              {/* @api POST /api/anagrafiche/articoli/formati {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
              <ComboTabellata
                label="Formato"
                value={filtri.formatoId ? parseInt(filtri.formatoId, 10) : null}
                onChange={(id) => setFiltri(prev => ({ ...prev, formatoId: String(id ?? '') }))}
                societaId={filterSocietaId ? parseInt(filterSocietaId, 10) : 0}
                endpoint="/api/anagrafiche/articoli/formati"
              />
            </div>

            <div className="articoli-filtro-flag">
              <span className="articoli-filtro-flag__label">E-commerce</span>
              <label
                className={`articoli-filtro-flag__toggle ${filtri.ecommerce === true ? 'articoli-filtro-flag__toggle--attivo' : ''}`}
              >
                <input
                  type="checkbox"
                  className="articoli-filtro-flag__checkbox"
                  checked={filtri.ecommerce === true}
                  onChange={(e) => setFiltri(prev => ({ ...prev, ecommerce: e.target.checked ? true : null }))}
                />
                {filtri.ecommerce === true ? 'Solo e-commerce' : 'Tutti gli articoli'}
              </label>
            </div>

            <div className="articoli-filtro-flag">
              <span className="articoli-filtro-flag__label">Preventivi</span>
              <label
                className={`articoli-filtro-flag__toggle ${filtri.perPreventivi === true ? 'articoli-filtro-flag__toggle--attivo' : ''}`}
              >
                <input
                  type="checkbox"
                  className="articoli-filtro-flag__checkbox"
                  checked={filtri.perPreventivi === true}
                  onChange={(e) => setFiltri(prev => ({ ...prev, perPreventivi: e.target.checked ? true : null }))}
                />
                {filtri.perPreventivi === true ? 'Solo per preventivi' : 'Tutti gli articoli'}
              </label>
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Stato</label>
              <select
                className="bop-field__select"
                value={filtri.stato}
                onChange={(e) => setFiltri(prev => ({ ...prev, stato: e.target.value as 'attivi' | 'tutti' | 'disattivi' }))}
              >
                <option value="attivi">Solo articoli attivi</option>
                <option value="tutti">Tutti gli articoli</option>
                <option value="disattivi">Solo articoli disattivi</option>
              </select>
            </div>
          </div>

          {/* Riga 3: Categ. Merceologica | Categ. Configurazione | Effetto */}
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">Categ. Merceologica</label>
              {/* @api GET /api/categorie-merceologiche -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
              {/* @api POST /api/categorie-merceologiche {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
              <ComboTabellata
                label="Categ. Merceologica"
                value={filtri.categoriaMerceologicaId}
                onChange={(id) => setFiltri(prev => ({ ...prev, categoriaMerceologicaId: id }))}
                societaId={filterSocietaId ? parseInt(filterSocietaId, 10) : 0}
                endpoint="/api/categorie-merceologiche"
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Categ. Configurazione</label>
              {/* @api GET /api/categorie-configurazione -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
              {/* @api POST /api/categorie-configurazione {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
              <ComboTabellata
                label="Categ. Configurazione"
                value={filtri.categoriaConfigurazioneId}
                onChange={(id) => setFiltri(prev => ({ ...prev, categoriaConfigurazioneId: id }))}
                societaId={filterSocietaId ? parseInt(filterSocietaId, 10) : 0}
                endpoint="/api/categorie-configurazione"
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">Effetto</label>
              {/* @api GET /api/effetti -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
              {/* @api POST /api/effetti {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
              <ComboTabellata
                label="Effetto"
                value={filtri.effettoId}
                onChange={(id) => setFiltri(prev => ({ ...prev, effettoId: id }))}
                societaId={filterSocietaId ? parseInt(filterSocietaId, 10) : 0}
                endpoint="/api/effetti"
              />
            </div>
          </div>

          {/* Riga 4: U.M. Vendita | U.M. Acquisto */}
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label">U.M. di vendita</label>
              <ComboFiltro
                label="U.M. di vendita"
                value={filtri.umVenditaId || null}
                onChange={(id) => setFiltri(prev => ({ ...prev, umVenditaId: id ?? '' }))}
                items={umList.map(u => ({ id: String(u.id), nome: `${u.simbolo} — ${u.nome}` }))}
              />
            </div>

            <div className="bop-field">
              <label className="bop-field__label">U.M. di acquisto</label>
              <ComboFiltro
                label="U.M. di acquisto"
                value={filtri.umAcquistoId || null}
                onChange={(id) => setFiltri(prev => ({ ...prev, umAcquistoId: id ?? '' }))}
                items={umList.map(u => ({ id: String(u.id), nome: `${u.simbolo} — ${u.nome}` }))}
              />
            </div>
          </div>

          <div className="articoli-filtri-actions">
            <button className="bop-btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button className="bop-btn-primary" onClick={handleCerca} disabled={loading}>
              {loading ? 'Ricerca...' : 'Cerca'}
            </button>
          </div>
        </div>

        {/* Tabella Excel-like (paginazione server-side gestita esternamente) */}
        <BopExcelTable<Articolo>
          data={articoli}
          loading={loading}
          rowKey={(a) => a.id}
          onRowClick={(a) => navigate(`/anagrafiche/articoli/${a.id}`)}
          hidePagination
          emptyState={<div className="bop-empty-state">Nessun articolo trovato</div>}
          columns={columns}
        />

        {total > 0 && (
          <div className="articoli-paginazione">
            <span className="bop-text-muted">
              {(currentPage - 1) * LIMIT + 1}–{Math.min(currentPage * LIMIT, total)} di {total} articoli
            </span>
            {totalPages > 1 && (
              <div className="articoli-paginazione-nav">
                <button
                  className="bop-btn-secondary bop-btn-sm"
                  disabled={currentPage <= 1 || loading}
                  onClick={() => doSearch(filtri, currentPage - 1)}
                >
                  ← Prec
                </button>
                <span className="bop-text-muted">{currentPage} / {totalPages}</span>
                <button
                  className="bop-btn-secondary bop-btn-sm"
                  disabled={currentPage >= totalPages || loading}
                  onClick={() => doSearch(filtri, currentPage + 1)}
                >
                  Succ →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal Creazione Articolo */}
        {showModal && (
          <div className="articoli-modal-overlay">
            <div className="articoli-modal" onClick={(e) => e.stopPropagation()}>
              <div className="articoli-modal-header">
                <h3 className="articoli-modal-title">Nuovo Articolo</h3>
                <button className="articoli-modal-close" onClick={handleCloseModal}>
                  <XIcon />
                </button>
              </div>

              <div className="articoli-modal-body">
                {error && (
                  <div className="articoli-modal-error">
                    {error}
                  </div>
                )}

                {/* Società */}
                {!isUnica && (
                  <div className="bop-form-group">
                    <label className="bop-label">
                      Società <span className="bop-text-danger">*</span>
                    </label>
                    <select
                      className="bop-select"
                      value={modalData.societaId}
                      onChange={(e) => setModalData({ ...modalData, societaId: e.target.value })}
                    >
                      <option value="">Seleziona società...</option>
                      {listaSocieta.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Tipologia */}
                <div className="bop-form-group">
                  <label className="bop-label">
                    Tipologia <span className="bop-text-danger">*</span>
                  </label>
                  <select
                    className="bop-select"
                    value={modalData.tipologia}
                    onChange={(e) => setModalData({ ...modalData, tipologia: e.target.value })}
                  >
                    <option value="">Seleziona tipologia...</option>
                    {TIPOLOGIE.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Prodotto (solo per tipologie ricorrenti — Nodo 1) */}
                {isRicorrente(modalData.tipologia) && (
                  <div className="bop-form-group">
                    <label className="bop-label">
                      Prodotto <span className="bop-text-danger">*</span>
                    </label>
                    <select
                      className="bop-select"
                      value={modalData.prodottoId ?? ''}
                      onChange={(e) => setModalData({
                        ...modalData,
                        prodottoId: e.target.value === '' ? null : Number(e.target.value)
                      })}
                    >
                      <option value="">Seleziona prodotto...</option>
                      {prodottiOptions.map(p => (
                        <option key={p.id} value={p.id}>{p.codice} — {p.nome}</option>
                      ))}
                    </select>
                    {prodottiOptions.length === 0 && (
                      <span className="articoli-modal-help-text">
                        Nessun prodotto in anagrafica per questa società. Creane uno prima
                        in Anagrafiche → Prodotti.
                      </span>
                    )}
                  </div>
                )}

                {/* Codice + Suggerisci */}
                <div className="bop-form-group">
                  <label className="bop-label">
                    Codice <span className="bop-text-danger">*</span>
                  </label>
                  <div className="articoli-codice-suggerisci-group">
                    <div className="articoli-codice-input-wrapper">
                      <input
                        type="text"
                        className="bop-input"
                        value={modalData.codice}
                        onChange={(e) => setModalData({ ...modalData, codice: e.target.value.toUpperCase() })}
                        placeholder="Es. PIAS"
                        maxLength={50}
                      />
                    </div>
                    <button
                      type="button"
                      className="bop-btn-secondary articoli-codice-suggerisci-btn"
                      onClick={handleSuggerisci}
                    >
                      Suggerisci
                    </button>
                  </div>
                  <span className="articoli-modal-help-text">
                    Digita una radice (es. PIAS) e clicca "Suggerisci" per il prossimo codice disponibile
                  </span>
                </div>

                {/* Descrizione base */}
                <div className="bop-form-group">
                  <label className="bop-label">
                    Descrizione base <span className="bop-text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="bop-input"
                    value={modalData.descrizione}
                    onChange={(e) => setModalData({ ...modalData, descrizione: e.target.value })}
                    placeholder="Es. Piastrella Gres Porcellanato"
                  />
                </div>

                {/* U.M. Vendita */}
                <div className="bop-form-group">
                  <label className="bop-label">
                    U.M. Vendita <span className="bop-text-danger">*</span>
                  </label>
                  <InlineSelect
                    value={modalData.umVenditaId || ''}
                    onChange={(id) => setModalData({ ...modalData, umVenditaId: id })}
                    opzioni={umList.map(u => ({ id: u.id, label: `${u.simbolo} — ${u.nome}` }))}
                    placeholder="Seleziona unità di misura..."
                    onCrea={async (simbolo) => {
                      const sid = modalData.societaId || filterSocietaId;
                      // @api POST /api/anagrafiche/articoli/unita-misura {"societaId":1,"simbolo":"mq","nome":"mq"} -> {"success":true,"data":{"id":1,"simbolo":"mq","nome":"mq"}}
                      const r = await apiFetch<{ success: boolean; data: UnitaMisura }>(
                        '/api/anagrafiche/articoli/unita-misura',
                        { method: 'POST', body: JSON.stringify({ societaId: parseInt(sid!), simbolo, nome: simbolo }) }
                      );
                      setUmList(prev => [...prev, r.data]);
                      return { id: r.data.id, label: `${r.data.simbolo} — ${r.data.nome}` };
                    }}
                  />
                </div>
              </div>

              <div className="articoli-modal-footer">
                <button className="bop-btn-secondary" onClick={handleCloseModal} disabled={saving}>
                  Annulla
                </button>
                <button
                  className={`bop-btn-primary ${saving ? 'articoli-modal-btn-saving' : ''}`}
                  onClick={handleCreaArticolo}
                  disabled={saving}
                >
                  {saving ? 'Creazione...' : 'Crea Articolo'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
